import {
  Avatar,
  Badge,
  Card,
  Descriptions,
  Flex,
  Menu,
  Skeleton,
  Statistic,
  Typography
} from 'antd'
import {
  Bot,
  Building2,
  FileText,
  Fingerprint,
  Home,
  LayoutGrid,
  Settings,
  Share2,
  Shield,
  Users
} from 'lucide-react'
import { memo, type ComponentType, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { AvatarWithInitials, MonoValue, SectionLabel } from 'src/components'

import { spotId } from '../constants'
import { useStyles } from '../styles'

const { Text, Title } = Typography

/**
 * Values shown in the mock. `null` means "not answered yet" and renders an AntD
 * skeleton — the mock never invents data.
 */
export type PreviewData = {
  workspaceName: string | null
  /**
   * Name the tile takes its colour and initials from — the same value as
   * `workspaceName`, but only once typing has paused. Kept apart so the heading
   * can stay live while the tile stops cycling colours mid-word.
   */
  tileName: string | null
  /** Local preview URL of the picked workspace picture, if any. */
  photoUrl?: string
  handle: string | null
  workspaceUrl: string | null
  primaryDomain: string | null
  userName: string | null
  /** Account id — feeds the avatar colour so it matches the real top bar. */
  userId?: number
  greeting: string | null
  role: string | null
  members: number | null
  apps: number | null
  roles: number | null
  authMethods: number | null
}

type Props = { data: PreviewData }

/** Placeholder bar for a value the user has not supplied yet. */
const Pill = ({ width, height = 10 }: { width: number; height?: number }) => (
  <Skeleton.Input
    style={{ width, minWidth: width, height, blockSize: height }}
  />
)

/** Real value once known, skeleton until then. */
const Value = ({
  known,
  width,
  children
}: {
  known: string | null
  width: number
  children: (value: string) => ReactNode
}) => (known ? <>{children(known)}</> : <Pill width={width} />)

const ACTIVITY_ROWS = [0, 1, 2, 3]

type NavIcon = ComponentType<{ size?: number }>

/** Width of the mock's sidebar. Narrow: the content half is what is on show. */
const SIDEBAR_WIDTH = 168

/**
 * Sidebar nav shape: one entry per real nav item, with the pill width standing
 * in for its label so the column reads like the actual sidebar. Widths are cut
 * to {@link SIDEBAR_WIDTH} minus the icon and the item's own padding.
 */
const NAV_GROUPS: Array<Array<{ icon: NavIcon; width: number }>> = [
  [
    { icon: Home, width: 44 },
    { icon: LayoutGrid, width: 50 }
  ],
  [
    { icon: Users, width: 68 },
    { icon: Building2, width: 74 },
    { icon: Shield, width: 38 },
    { icon: Fingerprint, width: 80 }
  ],
  [
    { icon: LayoutGrid, width: 65 },
    { icon: Bot, width: 46 }
  ],
  [
    { icon: FileText, width: 71 },
    { icon: Share2, width: 77 }
  ],
  [{ icon: Settings, width: 86 }]
]

/**
 * Memoised: hovering a form control only changes the spotlight target, and the
 * mock has no part in that. Without this the whole thing — menu, descriptions,
 * statistics, every skeleton — re-rendered on each mouse-enter.
 */
export const PreviewMock = memo(({ data }: Props) => {
  const { t } = useTranslation()
  const { styles } = useStyles()

  /**
   * The nav is deliberately unlabelled: it is chrome the user has not filled in,
   * so it stays skeletal like every other unanswered part of the mock. Icons and
   * group sizes are kept — they carry the shape of the real sidebar.
   */
  const navItems = NAV_GROUPS.map((group, groupIndex) => ({
    type: 'group' as const,
    key: `group-${groupIndex}`,
    label: <Pill width={56} height={7} />,
    children: group.map(({ icon: Icon, width }, itemIndex) => ({
      key: `item-${groupIndex}-${itemIndex}`,
      icon: <Icon size={14} />,
      label: <Pill width={width} height={8} />
    }))
  }))

  const named = data.workspaceName !== null

  /**
   * Same component the real sidebar and app rail use, so the tile the user sees
   * here is exactly the tile they will get — colour included, since it is
   * derived from the name rather than picked. Keyed off the settled name, not
   * the live one, so the colour does not cycle while the name is typed.
   *
   * A picture on its own is enough to draw it: the icon is the first field on
   * the step and the name the second, so uploading one and then reading the
   * preview is the ordinary order of events — and the tile went on showing an
   * empty grey square through it.
   */
  const workspaceTile = (size: number) =>
    data.tileName !== null || data.photoUrl ? (
      <AvatarWithInitials
        name={data.tileName ?? ''}
        shape="square"
        size={size}
        src={data.photoUrl}
      />
    ) : (
      <Avatar
        shape="square"
        size={size}
        style={{ background: 'var(--ant-color-fill-secondary)' }}
      />
    )

  return (
    <Flex style={{ height: '100%' }}>
      <Flex
        vertical
        gap={12}
        style={{
          width: SIDEBAR_WIDTH,
          flex: '0 0 auto',
          padding: 10,
          background: 'var(--ant-color-bg-container)',
          borderInlineEnd: '1px solid var(--ant-color-border-secondary)'
        }}
      >
        <Flex align="center" gap={8}>
          {/* Flex, not Avatar, carries the spotlight id: AntD's Avatar does not
              forward arbitrary DOM attributes. */}
          <Flex id={spotId('sidebarTile')} flex="0 0 auto">
            {workspaceTile(30)}
          </Flex>
          <Flex vertical gap={4} style={{ minWidth: 0 }}>
            <Value known={data.workspaceName} width={82}>
              {(value) => (
                <Text strong ellipsis>
                  {value}
                </Text>
              )}
            </Value>
            <Value known={data.handle} width={54}>
              {(value) => (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {value}
                </Text>
              )}
            </Value>
          </Flex>
        </Flex>

        <Menu
          mode="inline"
          selectedKeys={['overview']}
          items={navItems}
          style={{ borderInlineEnd: 'none', background: 'transparent' }}
        />
      </Flex>

      <Flex vertical flex={1} style={{ minWidth: 0 }}>
        {/* No search field: it is chrome the flow never asks about, and at this
            scale it only crowded the two things the preview does answer for —
            the workspace URL and who is signing in. Both sit on the right, as
            one cluster, rather than straddling an empty bar. */}
        <Flex
          align="center"
          justify="flex-end"
          gap={12}
          style={{
            height: 52,
            flex: '0 0 auto',
            paddingInline: 14,
            background: 'var(--ant-color-bg-container)',
            borderBottom: '1px solid var(--ant-color-border-secondary)'
          }}
        >
          <Flex id={spotId('urlPill')} align="center" flex="0 0 auto">
            <Value known={data.workspaceUrl} width={170}>
              {(value) => <MonoValue copyable={false}>{value}</MonoValue>}
            </Value>
          </Flex>

          <Flex id={spotId('userChip')} align="center" gap={8} flex="0 0 auto">
            {/* Round and keyed by `id`, exactly like the real top bar — the
                avatar colour is derived from `name-id`, so omitting the id
                would produce a different hue than the header the user sees. */}
            {data.userName !== null ? (
              <AvatarWithInitials
                name={data.userName}
                id={data.userId}
                size={28}
              />
            ) : (
              <Avatar
                size={28}
                style={{ background: 'var(--ant-color-fill-secondary)' }}
              />
            )}
            <Value known={data.userName} width={84}>
              {(value) => <Text>{value}</Text>}
            </Value>
          </Flex>
        </Flex>

        <Flex vertical flex={1} gap={14} style={{ padding: 16, minHeight: 0 }}>
          <Card styles={{ body: { padding: 0 } }}>
            <Flex
              id={spotId('hero')}
              align="center"
              gap={14}
              style={{ padding: 16 }}
            >
              <Flex flex="0 0 auto">{workspaceTile(52)}</Flex>
              <Flex vertical gap={6} style={{ minWidth: 0 }}>
                <Value known={data.greeting} width={186}>
                  {(value) => <Text type="secondary">{value}</Text>}
                </Value>
                <Value known={data.workspaceName} width={268}>
                  {(value) => (
                    <Title level={3} style={{ margin: 0 }}>
                      {value}
                    </Title>
                  )}
                </Value>
              </Flex>
            </Flex>

            <Descriptions
              bordered
              size="small"
              column={3}
              className={styles.previewMeta}
              styles={{ label: { width: 96 } }}
              items={[
                {
                  key: 'domain',
                  label: t('workspaces.onboarding.preview.meta.primaryDomain'),
                  children: (
                    <Value known={data.primaryDomain} width={96}>
                      {(value) => (
                        <Text id={spotId('domainMeta')} code>
                          {value}
                        </Text>
                      )}
                    </Value>
                  )
                },
                {
                  key: 'created',
                  label: t('workspaces.onboarding.preview.meta.created'),
                  children: (
                    <Value
                      known={
                        named
                          ? t('workspaces.onboarding.preview.meta.today')
                          : null
                      }
                      width={62}
                    >
                      {(value) => <Text>{value}</Text>}
                    </Value>
                  )
                },
                {
                  key: 'role',
                  label: t('workspaces.onboarding.preview.meta.yourRole'),
                  children: (
                    <Value known={data.role} width={54}>
                      {(value) => (
                        <Text id={spotId('roleMeta')} code>
                          {value}
                        </Text>
                      )}
                    </Value>
                  )
                }
              ]}
            />
          </Card>

          <Card
            title={
              <SectionLabel>
                {t('workspaces.onboarding.preview.stats.title')}
              </SectionLabel>
            }
            styles={{ body: { padding: 0 } }}
          >
            <Flex>
              <StatCell
                id={spotId('membersStat')}
                title={t('workspaces.onboarding.preview.stats.members')}
                count={data.members}
                divider
              />
              <StatCell
                id={spotId('appsStat')}
                title={t('workspaces.onboarding.preview.stats.apps')}
                count={data.apps}
                divider
              />
              <StatCell
                title={t('workspaces.onboarding.preview.stats.roles')}
                count={data.roles}
                divider
              />
              <StatCell
                id={spotId('authStat')}
                title={t('workspaces.onboarding.preview.stats.authMethods')}
                count={data.authMethods}
              />
            </Flex>
          </Card>

          <Card
            title={t('workspaces.onboarding.preview.activity')}
            // Takes whatever the stage's authored height leaves, so the mock
            // ends exactly on the card's bottom edge instead of on a strip of
            // dead space whose size depends on the rows above.
            style={{ flex: 1, minHeight: 0 }}
            styles={{ body: { padding: 0 } }}
          >
            {/* Plain rows, not `List` — AntD v6 deprecates that component. */}
            {ACTIVITY_ROWS.map((row) => (
              <div
                key={row}
                style={{
                  padding: '10px 16px',
                  borderTop:
                    row === 0
                      ? undefined
                      : '1px solid var(--ant-color-border-secondary)'
                }}
              >
                <Skeleton
                  avatar={{ size: 24, shape: 'square' }}
                  title={{ width: `${44 + row * 5}%` }}
                  paragraph={{ rows: 1, width: `${58 + row * 4}%` }}
                />
              </div>
            ))}
          </Card>
        </Flex>
      </Flex>
    </Flex>
  )
})

PreviewMock.displayName = 'PreviewMock'

type StatCellProps = {
  title: string
  count: number | null
  id?: string
  divider?: boolean
}

const StatCell = ({ title, count, id, divider }: StatCellProps) => (
  <Flex
    id={id}
    flex={1}
    style={{
      minWidth: 0,
      padding: '14px 16px',
      borderInlineEnd: divider
        ? '1px solid var(--ant-color-border-secondary)'
        : undefined
    }}
  >
    <Statistic
      title={<SectionLabel>{title}</SectionLabel>}
      value={count ?? 0}
      // `Badge` for the number itself: it rolls the digits when the count
      // changes, so adding an invite row is visible in the preview rather than
      // being a silent swap. Stripped of the badge's own skin — no pill, no
      // colour, no shadow — because this is a statistic, not a notification.
      valueRender={
        count === null
          ? () => <Pill width={34} height={16} />
          : () => (
              <Badge
                count={count}
                overflowCount={Number.MAX_SAFE_INTEGER}
                styles={{
                  indicator: {
                    background: 'transparent',
                    boxShadow: 'none',
                    color: 'var(--ant-color-text)',
                    // Explicit rather than `inherit`: the badge's own font-size
                    // is what would be inherited, and it is sized for a
                    // notification dot, not for a statistic.
                    fontSize: 'var(--ant-statistic-content-font-size, 24px)',
                    fontWeight: 'normal',
                    lineHeight: 1.2,
                    height: 'auto',
                    minWidth: 0,
                    padding: 0
                  }
                }}
              />
            )
      }
    />
  </Flex>
)
